import { PageId, SubscriptionStatus } from "../types";
import { deriveTenantAdmin } from "../hooks/useTenantAdmin";
import { describeAdminGate } from "./adminGate";
import { ADMIN_PAGES, canOpenAdminPage, isAdminPage } from "./adminPages";
import { isProviderRole } from "./tenantAccess";
import { protectedModelOf } from "./protectedModel";

// ENG-3479 — one console per role.
//
// An admin used to get two consoles at once: "My Haven" (Home, Settings, Secure Sites, …) above the
// organization's pages, with two landing pages (Home and Overview) and org concerns under the
// personal header (Secure Sites edits the org's protection, Settings held its billing — ENG-2656).
// Now an owner or admin gets the organization's console only, and everyone else keeps the member
// console exactly as it was.
//
// Which console a person gets is decided here, once, and read by the nav, the landing redirect, the
// account menu, the page width, the org header and App's page-view tracking. Pure, like adminGate, so
// every branch is assertable without a render.

/**
 * - `pending`: /subscription-status has not answered and has not failed. The member nav shows with
 *   "Checking your organization…" (ENG-2534); nothing admin-only is offered, and nothing redirects.
 * - `org`: an owner or admin of an organization, INCLUDING one whose licence lapsed; or a provider's
 *   operator in a client organization with an active licence (ENG-3407), whatever their tier. The lapsed admin
 *   has no admin tools (deriveTenantAdmin), but must keep Billing and the switcher — the ENG-2555 /
 *   ENG-2553 escape hatches — so they get the organization's console with only those in it.
 * - `member`: everyone else — a member, a solo user, a personal subscriber, an admin role in a tenant
 *   type with no console, and a gate that FAILED (ENG-2857 keeps the member nav plus a retry).
 */
export type ConsoleKind = "pending" | "member" | "org";

/**
 * An owner or admin whose organization's licence is not active: the admin tools are gone, and
 * describeAdminGate says why. Billing is exactly what they need, so they stay in the org console.
 *
 * ENG-3407: not a provider's operator in a lapsed client. describeAdminGate explains that to them
 * too, but billing is never a provider's (`mayManageBilling`), so the org console would hold nothing
 * they can use; they keep the member console, with the notice.
 */
export const isLapsedAdmin = (status: SubscriptionStatus | null | undefined): boolean =>
  describeAdminGate(status)?.reason === "subscription_inactive" && !isProviderRole(status?.role);

/**
 * ENG-3407: whether this person may open the organization's Billing page. Everyone in the org console
 * except a provider's operator: the backend refuses them the billing portal and adding seats at every
 * tier (`mayManageBilling`, D3), so the page would offer only doors that answer 403.
 */
export const opensBilling = (status: SubscriptionStatus | null | undefined, kind: ConsoleKind): boolean =>
  kind === "org" && !isProviderRole(status?.role);

export function consoleKindFor(
  status: SubscriptionStatus | null | undefined,
  loaded: boolean,
  failed: boolean,
): ConsoleKind {
  if (!loaded) return failed ? "member" : "pending";
  if (deriveTenantAdmin(status).isTenantAdmin || isLapsedAdmin(status)) return "org";
  return "member";
}

/**
 * Pages whose destination depends on the console, so nothing is shown (or counted as a page view)
 * for them until the console is known.
 *
 * ENG-3526: the organization's pages too. Whether the viewer can open one is not known until the
 * console is, and before this the member's Home showed in their place while it loaded.
 */
const DEPENDS_ON_CONSOLE: readonly PageId[] = ["home", "billing", ...ADMIN_PAGES];

/**
 * Whether [page] can be shown yet, or must wait for the console to be known.
 *
 * [gateFailed]: /subscription-status gave up. The console is the member's then (ENG-2857), and Home
 * shows as it always has. Billing does not: sending it to Settings on a failure would rewrite the URL
 * of an admin who asked for Billing, and a retry that succeeds would no longer land them there. It
 * waits, and the layout offers the retry.
 */
export const landingDecided = (page: PageId, kind: ConsoleKind, gateFailed = false): boolean =>
  !DEPENDS_ON_CONSOLE.includes(page) || (kind !== "pending" && !(page === "billing" && gateFailed));

/**
 * Where [page] really lives for this person, or null when it is shown as asked.
 *
 * - `home` for an admin who can open Overview is Overview: the organization console has one landing
 *   page. An admin without it (family, lapsed) keeps Home, which is where the lapsed-admin notice is.
 * - `billing` for anyone outside the org console is Settings, where a personal subscriber's billing
 *   is (the desktop app's `#subscribe` link resolves to billing). So it is for a provider's operator,
 *   whose console has no Billing (ENG-3407, `opensBilling`).
 *
 * - an organization page the viewer cannot open, in the org console: see [accessRedirect].
 *
 * Nothing redirects while the console is pending: the answer is not known yet. Nor does Billing
 * after the gate failed (see [landingDecided]).
 */
export function landingRedirect(
  page: PageId,
  status: SubscriptionStatus | null | undefined,
  kind: ConsoleKind,
  gateFailed = false,
): PageId | null {
  if (kind === "pending") return null;
  if (page === "billing" && gateFailed) return null;
  const denied = accessRedirect(page, status, kind);
  if (denied) return denied;
  if (page === "home" && kind === "org" && deriveTenantAdmin(status).capabilities.canViewDashboard) return "overview";
  if (page === "billing" && !opensBilling(status, kind)) return "settings";
  return null;
}

/**
 * ENG-3526: where the org console sends an organization page [page] the viewer cannot open, or null
 * when they can open it (or it is not the org console's to decide).
 *
 * Their landing page: Overview, or Home for an admin whose tier has no Overview (family). It used to
 * render the member's Home in place, under the page's URL with nothing selected in the nav, and the
 * page's Related rail under it. The easy way there is a switch: a provider on Policy in one client
 * switches to another where they are Read only, and the reload keeps `#policy`.
 *
 * Only for someone the console is open to (`isTenantAdmin`). An admin whose licence lapsed is in the
 * org console with no pages at all, and the admin-gate notice says why where they asked; the member
 * console keeps its own fallbacks (Home, or Settings for `#users`).
 */
export function accessRedirect(
  page: PageId,
  status: SubscriptionStatus | null | undefined,
  kind: ConsoleKind,
): PageId | null {
  // The organization's own pages (adminPages ADMIN_PAGES), the ones canOpenAdminPage answers for.
  if (kind !== "org" || !isAdminPage(page)) return null;
  const { isTenantAdmin, capabilities } = deriveTenantAdmin(status);
  // ENG-4063: Policy depends on the Protected model, which the same status carries.
  if (!isTenantAdmin || canOpenAdminPage(page, capabilities, protectedModelOf(status?.protectedModel))) return null;
  return canOpenAdminPage("overview", capabilities) ? "overview" : "home";
}
